import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { api } from '../api/client'
import { trackTripCreated } from '../lib/analytics'
import OnboardingModal from '../components/OnboardingModal'
import {
  Users, Plus, Trash2, UserPlus,
  ChevronRight, X, Check, Calendar, Plane, Home,
} from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Format two ISO date strings (e.g. "2026-01-05", "2026-01-19") into
 * a human-readable range like "Jan 5 – Jan 19, 2026".
 */
function formatDateRange(start, end) {
  const fmt = (iso) => {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  const [y] = end.split('-').map(Number)
  return `${fmt(start)} – ${fmt(end)}, ${y}`
}

// ── Category color map ───────────────────────────────────────────────────────
const MEMBER_COLORS = [
  'bg-lime-400 text-ink-950',
  'bg-green-400 text-ink-950',
  'bg-amber-400 text-ink-950',
  'bg-red-400 text-white',
  'bg-blue-400 text-white',
]

function MemberAvatar({ name, index }) {
  const colorClass = MEMBER_COLORS[index % MEMBER_COLORS.length]
  const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${colorClass}`}>
      {initials}
    </div>
  )
}

function AddMemberInline({ groupId, onDone, prefill = '' }) {
  // Pre-fill with the caller's suggestion (typically the user's own display name
  // when they're adding themselves as the first member of a new trip).
  const [name, setName] = useState(prefill)
  const qc = useQueryClient()

  const mut = useMutation({
    mutationFn: (n) => api.addMember(groupId, n),
    onSuccess: () => {
      qc.invalidateQueries(['groups'])
      qc.invalidateQueries(['group', String(groupId)])
      setName('')
      onDone?.()
    },
  })

  const submit = (e) => {
    e.preventDefault()
    if (name.trim()) mut.mutate(name.trim())
  }

  return (
    <form onSubmit={submit} className="flex gap-2 mt-2">
      <input
        className="input flex-1 text-sm py-1.5"
        placeholder="Member name…"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      <button type="submit" className="btn-primary py-1.5 px-3" disabled={!name.trim() || mut.isPending}>
        <Check size={14} />
      </button>
      <button type="button" className="btn-ghost py-1.5 px-2" onClick={onDone}>
        <X size={14} />
      </button>
    </form>
  )
}

function GroupCard({ group }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [addingMember, setAddingMember] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const deleteMember = useMutation({
    mutationFn: (id) => api.deleteMember(id),
    onSuccess: () => qc.invalidateQueries(['groups']),
  })

  const deleteGroup = useMutation({
    mutationFn: () => api.deleteGroup(group.id),
    onSuccess: () => qc.invalidateQueries(['groups']),
  })

  // Type-aware framing: the card must read differently for a trip (has a
  // finish line) vs a household (ongoing ledger). A household never says
  // "trip" anywhere — that was a reported bug.
  const isHousehold = group.kind === 'household'

  return (
    <div data-testid="group-card" className="bg-ink-900 border border-ink-700 rounded-2xl overflow-hidden animate-slide-up hover:border-ink-600 transition-all duration-200">

      {/* Clickable header — takes you straight into the group */}
      <div
        className="px-5 pt-5 pb-4 cursor-pointer group"
        onClick={() => navigate(`/groups/${group.id}`)}
      >
        {/* Top row: name + kind badge + delete */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="font-display text-xl font-semibold text-ink-50 group-hover:text-lime-400 transition-colors truncate">
                {group.name}
              </h3>
              {/* Kind badge — the at-a-glance Trip/Household distinction */}
              <span className={clsx(
                'flex items-center gap-1 flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded',
                isHousehold
                  ? 'bg-sky-400/10 text-sky-300 border border-sky-400/25'
                  : 'bg-lime-400/10 text-lime-300 border border-lime-400/25',
              )}>
                {isHousehold ? <Home size={9} /> : <Plane size={9} />}
                {isHousehold ? 'Household' : 'Trip'}
              </span>
            </div>
            {/* Subline: trips show their dates; households are ongoing by
                definition — "No dates set" would be a trip-shaped nag. */}
            {isHousehold ? (
              <p className="text-xs text-ink-500 font-mono mt-1">Ongoing — settle anytime</p>
            ) : group.start_date && group.end_date ? (
              <p className="text-xs text-lime-400/70 font-mono mt-1 flex items-center gap-1.5">
                <Calendar size={10} />
                {formatDateRange(group.start_date, group.end_date)}
              </p>
            ) : (
              <p className="text-xs text-ink-500 font-mono mt-1">No dates set</p>
            )}
          </div>

          {/* Delete control — stops propagation so clicking it doesn't open the trip */}
          <div
            className="flex items-center gap-1 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {!confirmDelete ? (
              <button
                className="btn-ghost p-1.5 text-ink-500 hover:text-red-400"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 size={13} />
              </button>
            ) : (
              <div className="flex items-center gap-1">
                {/* Feedback matters here: previously a failed delete changed
                    nothing on screen, so it looked "stuck". Now we show the
                    error, and disable + relabel the button while it's working. */}
                {deleteGroup.isError ? (
                  <span className="text-xs text-red-400" title={deleteGroup.error?.message}>
                    Couldn't delete — try again
                  </span>
                ) : (
                  <span className="text-xs text-ink-400">Delete?</span>
                )}
                <button
                  className="btn-danger py-0.5 px-2 text-xs disabled:opacity-50"
                  disabled={deleteGroup.isPending}
                  onClick={() => deleteGroup.mutate()}
                >
                  {deleteGroup.isPending ? 'Deleting…' : 'Yes'}
                </button>
                <button
                  className="btn-ghost py-0.5 px-2 text-xs"
                  disabled={deleteGroup.isPending}
                  onClick={() => { setConfirmDelete(false); deleteGroup.reset() }}
                >
                  No
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Member avatar cluster */}
        <div className="flex items-center gap-2">
          {group.members.length === 0 ? (
            <span className="text-xs text-ink-500 italic">No members yet</span>
          ) : (
            <>
              {/* Stack up to 4 avatars with a slight overlap */}
              <div className="flex -space-x-2">
                {group.members.slice(0, 4).map((m, i) => (
                  <div
                    key={m.id}
                    className={`w-7 h-7 rounded-full border-2 border-ink-900 flex items-center justify-center text-[10px] font-bold ${MEMBER_COLORS[i % MEMBER_COLORS.length]}`}
                    title={m.name}
                  >
                    {m.name[0].toUpperCase()}
                  </div>
                ))}
                {group.members.length > 4 && (
                  <div className="w-7 h-7 rounded-full border-2 border-ink-900 bg-ink-700 flex items-center justify-center text-[9px] font-bold text-ink-300">
                    +{group.members.length - 4}
                  </div>
                )}
              </div>
              <span className="text-xs text-ink-500">
                {group.members.map(m => m.name).join(', ')}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="px-5 pb-4 flex items-center gap-2">
        {/* Open — neutral label; the kind badge above already says what it is */}
        <button
          className="btn-primary flex-1 justify-center text-sm py-2"
          onClick={() => navigate(`/groups/${group.id}`)}
        >
          Open
          <ChevronRight size={14} />
        </button>

        {/* Expand/collapse members panel */}
        <button
          className="btn-secondary py-2 px-3 text-xs"
          onClick={() => setExpanded(!expanded)}
          title="Manage members"
        >
          <Users size={13} />
        </button>
      </div>

      {/* Collapsible members management panel */}
      {expanded && (
        <div className="px-5 pb-4 border-t border-ink-800 pt-4 space-y-2">
          {group.members.map((m, i) => (
            <div key={m.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-ink-800/50 group">
              <MemberAvatar name={m.name} index={i} />
              <span className="flex-1 text-sm text-ink-200 font-medium">{m.name}</span>
              <button
                className="text-ink-500 hover:text-red-400 transition-all"
                onClick={() => deleteMember.mutate(m.id)}
              >
                <X size={13} />
              </button>
            </div>
          ))}

          {addingMember ? (
            <AddMemberInline
              groupId={group.id}
              onDone={() => setAddingMember(false)}
              prefill={group.members.length === 0 ? (localStorage.getItem('autosplit_display_name') || '') : ''}
            />
          ) : (
            <button
              className="btn-ghost w-full justify-center py-2 border border-dashed border-ink-700 text-xs"
              onClick={() => setAddingMember(true)}
            >
              <UserPlus size={13} />
              Add member
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// All supported currencies with their display labels
// These are the same currencies available in the expense entry form
const CURRENCIES = [
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'NZD', label: 'NZD — New Zealand Dollar' },
  { code: 'JPY', label: 'JPY — Japanese Yen' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'SGD', label: 'SGD — Singapore Dollar' },
  { code: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { code: 'THB', label: 'THB — Thai Baht' },
  { code: 'MXN', label: 'MXN — Mexican Peso' },
]

function CreateGroupForm({ onDone }) {
  const [name, setName] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [baseCurrency, setBaseCurrency] = useState('USD')  // default to USD
  // 'trip' = vacation/event with end date · 'household' = ongoing roommate / couple ledger
  const [kind, setKind] = useState('trip')
  const qc = useQueryClient()
  const navigate = useNavigate()

  const create = useMutation({
    mutationFn: () => api.createGroup(
      name.trim(),
      kind === 'household' ? null : (startDate || null),
      kind === 'household' ? null : (endDate || null),
      baseCurrency,
      kind,
    ),
    onSuccess: (data) => {
      qc.invalidateQueries(['groups'])
      trackTripCreated(kind)
      navigate(`/groups/${data.id}`, { state: { newTrip: true } })
    },
  })

  const isHousehold = kind === 'household'

  // Submitting without a name explains itself instead of silently doing
  // nothing (same fix as the expense modals' "still needed" checklist).
  const [attempted, setAttempted] = useState(false)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!name.trim()) { setAttempted(true); return }
        create.mutate()
      }}
      className="card border-lime-400/30 animate-slide-up"
    >
      <h3 className="font-display text-lg font-semibold text-ink-50 mb-3">
        {isHousehold ? 'New Household' : 'New Trip'}
      </h3>

      {/* ── Kind toggle: Trip vs Household ─────────────────────────────────
          Two distinct mental models. Trip = vacation, ends, settles once.
          Household = roommate / partner ledger that runs forever, gets
          settled monthly. UI elements adapt below based on this choice. */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          type="button"
          onClick={() => setKind('trip')}
          className={clsx(
            'flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all text-left',
            kind === 'trip'
              ? 'border-lime-400/50 bg-lime-400/8'
              : 'border-ink-700 hover:border-ink-500 bg-ink-800/30',
          )}
        >
          <div className={clsx(
            'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
            kind === 'trip' ? 'bg-lime-400/15' : 'bg-ink-800',
          )}>
            <Plane size={14} className={kind === 'trip' ? 'text-lime-400' : 'text-ink-400'} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink-100">Trip</div>
            <div className="text-[10px] text-ink-500 leading-tight">Has dates · settle at the end</div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setKind('household')}
          className={clsx(
            'flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all text-left',
            kind === 'household'
              ? 'border-lime-400/50 bg-lime-400/8'
              : 'border-ink-700 hover:border-ink-500 bg-ink-800/30',
          )}
        >
          <div className={clsx(
            'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
            kind === 'household' ? 'bg-lime-400/15' : 'bg-ink-800',
          )}>
            <Home size={14} className={kind === 'household' ? 'text-lime-400' : 'text-ink-400'} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink-100">Household</div>
            <div className="text-[10px] text-ink-500 leading-tight">Ongoing · rent &amp; bills monthly</div>
          </div>
        </button>
      </div>

      {/* Group name (placeholder adapts to kind) */}
      <input
        className={clsx(
          'input mb-1.5',
          attempted && !name.trim() && 'border-red-400/60 ring-1 ring-red-400/30'
        )}
        placeholder={isHousehold
          ? 'e.g. Apartment, Our place, The Loft…'
          : 'e.g. Japan 2026, Ski Trip, Mexico…'}
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      {attempted && !name.trim() && (
        <p role="alert" className="text-xs text-amber-300 mb-2">
          Give it a name first — that's all that's missing.
        </p>
      )}
      <div className="mb-1.5" />

      {/* Settlement currency — what currency do you want all expenses tracked in? */}
      <div className="mb-4">
        <label className="block text-xs text-ink-400 mb-1.5">
          Settlement currency
        </label>
        <p className="text-xs text-ink-500 mb-2">
          All expenses in other currencies will be converted to this for the final split
        </p>
        <select
          className="select w-full text-sm"
          value={baseCurrency}
          onChange={(e) => setBaseCurrency(e.target.value)}
        >
          {CURRENCIES.map(c => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Trip dates — hidden entirely in household mode (a household has no
          end date by definition). Stacked layout on mobile to dodge the
          iOS Safari 16px input width overflow. */}
      {!isHousehold && (
        <div className="mb-4">
          <label className="block text-xs text-ink-400 mb-1.5 flex items-center gap-1.5">
            <Calendar size={11} />
            Trip dates <span className="text-ink-500">(optional)</span>
          </label>
          <p className="text-xs text-ink-500 mb-3">
            Everyday spending outside these dates is <strong className="text-amber-400/90">auto-excluded</strong>. Flights, hotels, and Airbnbs booked up to 90 days before are <strong className="text-lime-400/90">flagged for review</strong> — so pre-trip bookings don't disappear.
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex-1 min-w-0">
              <span className="block text-[10px] text-ink-500 uppercase tracking-widest mb-1 sm:hidden">
                Start
              </span>
              <input
                type="date"
                className="input w-full text-sm [color-scheme:dark]"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
                aria-label="Trip start date"
              />
            </div>
            <span className="hidden sm:inline text-ink-500 text-sm shrink-0">–</span>
            <div className="flex-1 min-w-0">
              <span className="block text-[10px] text-ink-500 uppercase tracking-widest mb-1 sm:hidden">
                End
              </span>
              <input
                type="date"
                className="input w-full text-sm [color-scheme:dark]"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                aria-label="Trip end date"
              />
            </div>
          </div>
        </div>
      )}

      {/* Tiny helper note shown in Household mode to set expectations */}
      {isHousehold && (
        <p className="text-[11px] text-ink-500 mb-4 leading-relaxed">
          Households are ongoing — no end date. Add expenses as they happen, settle up monthly (or whenever you both agree).
        </p>
      )}

      <div className="flex gap-2">
        <button type="submit" className={clsx('btn-primary', !name.trim() && 'opacity-70')} disabled={create.isPending}>
          <Plus size={14} />
          {create.isPending
            ? 'Creating…'
            : isHousehold ? 'Create Household' : 'Create Trip'}
        </button>
        <button type="button" className="btn-ghost" onClick={onDone}>Cancel</button>
      </div>
    </form>
  )
}

export default function GroupsPage() {
  const [creating, setCreating] = useState(false)
  const navigate = useNavigate()

  // Show onboarding modal to first-time users.
  // Once they dismiss it, localStorage tracks that they've seen it.
  const [showOnboarding, setShowOnboarding] = useState(false)
  useEffect(() => {
    if (!localStorage.getItem('autosplit_onboarded')) {
      // Small delay so the page loads first before the modal pops up
      const t = setTimeout(() => setShowOnboarding(true), 600)
      return () => clearTimeout(t)
    }
  }, [])

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: api.getGroups,
  })

  // After Google OAuth, the user lands here. If they had a pending invite,
  // redirect them back to complete the join flow.
  useEffect(() => {
    const pendingJoin = sessionStorage.getItem('pendingJoin')
    if (pendingJoin) {
      sessionStorage.removeItem('pendingJoin')
      navigate(`/join/${pendingJoin}`, { replace: true })
    }
  }, [navigate])

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-ink-50 tracking-tight">Groups</h1>
          <p className="text-ink-400 text-sm mt-1">
            Trips and households — all your shared expenses in one place
          </p>
        </div>
        <button className="btn-primary" onClick={() => setCreating(!creating)}>
          <Plus size={15} />
          New Group
        </button>
      </div>

      {/* Create form */}
      {creating && (
        <div className="mb-6">
          <CreateGroupForm onDone={() => setCreating(false)} />
        </div>
      )}

      {/* Groups grid */}
      {isLoading ? (
        <div className="text-ink-500 text-sm animate-pulse-soft">Loading groups…</div>
      ) : groups.length === 0 && !creating ? (
        <div className="space-y-6 max-w-xl mx-auto">
          {/* Main CTA card */}
          <div className="card text-center py-12">
            <div className="w-14 h-14 rounded-2xl bg-lime-400/10 border border-lime-400/20 flex items-center justify-center mx-auto mb-5">
              <Users size={24} className="text-lime-400" />
            </div>
            <p className="font-display text-2xl text-ink-100 mb-2">Create your first group</p>
            <p className="text-ink-400 text-sm mb-6 leading-relaxed max-w-xs mx-auto">
              A trip with friends or a household with your partner or roommates — add the people, add the expenses, and AutoSplit figures out who owes whom.
            </p>
            <button className="btn-primary mx-auto" onClick={() => setCreating(true)}>
              <Plus size={14} />
              New Group
            </button>
          </div>

          {/* How it works — 3-step mini guide */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { step: '1', title: 'Create a group', desc: 'A trip (has dates, settle at the end) or a household (ongoing rent & bills)' },
              { step: '2', title: 'Add expenses', desc: 'Upload a statement or bank-app screenshot, snap a receipt, or type one in — your call' },
              { step: '3', title: 'Settle up', desc: 'AutoSplit calculates the fairest way to balance everything' },
            ].map(({ step, title, desc }) => (
              <div key={step} className="card-sm text-center">
                <div className="w-7 h-7 rounded-full bg-ink-700 border border-ink-600 flex items-center justify-center mx-auto mb-3">
                  <span className="text-xs font-bold font-mono text-lime-400">{step}</span>
                </div>
                <p className="text-sm font-medium text-ink-200 mb-1">{title}</p>
                <p className="text-xs text-ink-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groups.map((g, i) => (
            <div key={g.id} style={{ animationDelay: `${i * 60}ms` }}>
              <GroupCard group={g} />
            </div>
          ))}
        </div>
      )}

      {/* Onboarding modal — shown once to first-time users */}
      {showOnboarding && <OnboardingModal onClose={() => setShowOnboarding(false)} />}
    </div>
  )
}
